package com.alpha.showcase.common.ui.play

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.background
import androidx.compose.foundation.focusable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.PagerState
import androidx.compose.foundation.pager.VerticalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.rounded.ArrowBackIos
import androidx.compose.material.icons.automirrored.rounded.ArrowForwardIos
import androidx.compose.material3.Card
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.RectangleShape
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.lerp
import com.alpha.showcase.common.ui.settings.SHOWCASE_MODE_SLIDE
import isDesktop
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import showcaseapp.composeapp.generated.resources.Res
import showcaseapp.composeapp.generated.resources.backward
import showcaseapp.composeapp.generated.resources.forward
import kotlin.math.absoluteValue

@Composable
fun SlideImagePager(
  pagingItems: PagingPlayItems,
  vertical: Boolean = false,
  fitSize: Boolean = false,
  switchDuration: Long = DEFAULT_PERIOD,
  showProgress: Boolean = true,
  showContentInfo: Boolean? = null
) {

  var currentData by remember {
    mutableStateOf<Any?>(null)
  }

  // Finite pager: expose the CONTROLLER's count, not the live size. A dataset
  // shrink is adopted only while idle and after re-anchoring, so PagerState never
  // clamps the current page to a different image mid-show.
  val countController = rememberFinitePagerCountController(pagingItems) { currentData }
  val pagerState = rememberPagerState {
    countController.displayCount
  }
  LaunchedEffect(countController, pagerState) {
    countController.observeAndReconcile(pagerState)
  }
  val mediaStates = rememberMediaItemStateStore(fitSize)
  val overlayConfig = MediaOverlayConfig.forStyle(SHOWCASE_MODE_SLIDE)
  val focusRequester = remember { FocusRequester() }

  var showOpButton by remember { mutableStateOf(false) }

  LaunchedEffect(showOpButton) {
    if (showOpButton) {
      delay(5000) // Wait for 10 seconds
      showOpButton = false // Hide the button
    }
  }

  PagerMediaViewport(
    state = pagerState,
    modifier = Modifier.fillMaxSize(),
    vertical = vertical,
    onInteraction = {
      showOpButton = true
      val page = pagerState.currentPage
      mediaStates.get(page, countController.item(page), fitSize).interact(overlayConfig)
    },
  ) {
    if (vertical) {
      VerticalPager(
        state = pagerState, modifier = Modifier
          .fillMaxSize()
          .focusRequester(focusRequester)
      ) { page ->

        // Calculate the absolute offset for the current page from the
        // scroll position. We use the absolute value which allows us to mirror
        // any effects for both directions
        val pageOffset = ((pagerState.currentPage - page) + pagerState.currentPageOffsetFraction).absoluteValue
        PagerCard(pageOffset) {
          val mediaState = mediaStates.get(page, countController.item(page), fitSize)
          PagerItem(
            state = mediaState,
            active = page == pagerState.currentPage,
            onInteraction = { mediaState.interact(overlayConfig, it) },
          ) {
            currentData = it
            showOpButton = false
          }
        }
      }
    } else {
      HorizontalPager(
        state = pagerState, modifier = Modifier
          .fillMaxSize()
          .focusRequester(focusRequester)
      ) { page ->

        // Calculate the absolute offset for the current page from the
        // scroll position. We use the absolute value which allows us to mirror
        // any effects for both directions
        val pageOffset = ((pagerState.currentPage - page) + pagerState.currentPageOffsetFraction).absoluteValue
        PagerCard(pageOffset) {
          val mediaState = mediaStates.get(page, countController.item(page), fitSize)
          PagerItem(
            state = mediaState,
            active = page == pagerState.currentPage,
            onInteraction = { mediaState.interact(overlayConfig, it) },
          ) {
            currentData = it
          }
        }
      }
    }


    MediaOverlayTransition(
        mediaStates.get(pagerState.currentPage, countController.item(pagerState.currentPage), fitSize),
        SHOWCASE_MODE_SLIDE, showContentInfo,
        summaryVisible = !pagerState.isScrollInProgress,
    )
    val progressDuration = switchDuration.takeIf { it > 0 } ?: DEFAULT_PERIOD
    val progress = rememberPagerImagePlaybackProgress(
        pagerState, switchDuration, pagingItems.size, animationMillis = 1500,
    ) { page -> mediaStates.get(page, countController.item(page), fitSize) }
    PlaybackProgressBar(
        elapsedMillis = { progress.value * progressDuration },
        durationMillis = progressDuration,
        visible = showProgress && !pagerState.isScrollInProgress
            && pagingItems.size > 1 && !countController.item(pagerState.currentPage).isVideo(),
        modifier = Modifier.align(Alignment.BottomCenter),
    )

    ChangePage(pagerState, showOpButton)
  }
}

@Composable
fun ChangePage(
  pagerState: PagerState,
  show: Boolean
) {
  val animationScope = rememberCoroutineScope()
  ChangePage(
    show = show,
    canScrollForward = pagerState.canScrollForward,
    canScrollBackward = pagerState.canScrollBackward,
    onForward = {
      if (!pagerState.isScrollInProgress) {
        animationScope.launch {
          if (pagerState.canScrollForward) {
            pagerState.animateScrollToPage(pagerState.currentPage + 1, animationSpec = tween(1000))
          }
        }
      }
    },
    onBackward = {
      if (!pagerState.isScrollInProgress) {
        animationScope.launch {
          if (pagerState.canScrollBackward) {
            pagerState.animateScrollToPage(pagerState.currentPage - 1, animationSpec = tween(1000))
          }
        }
      }
    },
  )
}

@Composable
fun ChangePage(
  show: Boolean,
  canScrollForward: Boolean,
  canScrollBackward: Boolean,
  onForward: () -> Unit,
  onBackward: () -> Unit,
) {
  if (isDesktop()){
    Box(modifier = Modifier.fillMaxSize()) {
      AnimatedVisibility(
        show && canScrollForward,
        modifier = Modifier.align(Alignment.CenterEnd),
        enter = fadeIn(),
        exit = fadeOut()
      ){
        IconButton(
          onClick = onForward,
          modifier = Modifier.padding(30.dp).focusable().background(Color.Gray.copy(0.5f), shape = CircleShape)
        ) {
          Icon(
            imageVector = Icons.AutoMirrored.Rounded.ArrowForwardIos,
            contentDescription = stringResource(Res.string.forward),
            tint = Color.Black.copy(0.5f)
          )
        }
      }

      AnimatedVisibility(
        show && canScrollBackward,
        modifier = Modifier.align(Alignment.CenterStart),
        enter = fadeIn(),
        exit = fadeOut()
      ){
        IconButton(
          onClick = onBackward,
          modifier = Modifier.padding(30.dp).focusable().background(Color.Gray.copy(0.5f), shape = CircleShape)
        ) {
          Icon(
            imageVector = Icons.AutoMirrored.Rounded.ArrowBackIos,
            contentDescription = stringResource(Res.string.backward),
            tint = Color.Black.copy(0.5f)
          )
        }
      }
    }
  }

}

@Composable
fun PagerCard(pageOffset: Float, content: @Composable ColumnScope.() -> Unit) {
  Card(
    Modifier
      .graphicsLayer {
        // Calculate the absolute offset for the current page from the
        // scroll position. We use the absolute value which allows us to mirror
        // any effects for both directions
        // We animate the scaleX + scaleY, between 85% and 100%
        lerp(
            0.85f,
            1f,
            1f - pageOffset.coerceIn(0f, 1f)
          )
          .also { scale ->
            scaleX = scale
            scaleY = scale
          }

        // We animate the alpha, between 50% and 100%
        alpha = lerp(
          0.5f,
          1f,
          1f - pageOffset.coerceIn(0f, 1f)
        )
      }
      .fillMaxSize(),
    shape = RectangleShape) {
    content()
  }
}
